import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DateTime } from 'luxon';
import { FormProvider, useForm } from 'react-hook-form';

import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';

import { Crew } from '../Crew';
import { WeeklyAssignmentEditor } from '../WeeklyAssignmentEditor';
import { defaultFormValues as defaultValues } from './defaultFormValues';
import { FormValues } from './FormValues';
import {
  validateRangeEndDate,
  validateRangeStartDate,
} from './validateApplyDateRange';

export function MultiAssignmentManagementFormStep2({
  onSubmit,
  onCancel,
  children,
  loading,
  serverErrors,
  crew,
}: {
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
  children?: React.ReactNode;
  loading: boolean;
  serverErrors: string | null;
  crew?: Crew;
}) {
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      ...defaultValues,
      ...crew,
    },
  });

  const { handleSubmit, formState, getValues, watch } = methods;
  const { isValid, errors } = formState;

  const today = DateTime.local().toFormat('yyyy-MM-dd');
  const startDateValue = watch('applyDateRange.start');
  const endDateValue = watch('applyDateRange.end');
  const maxEndDate = startDateValue
    ? DateTime.fromISO(startDateValue).plus({ months: 2 }).toFormat('yyyy-MM-dd')
    : undefined;

  return (
    <FormProvider {...methods}>
      <form
        className="p-4"
        onSubmit={handleSubmit((val) => {
          onSubmit(getValues());
        })}
        style={{ maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div id="form-errors">
          {!!serverErrors && (
            <FormErrorMessage>{serverErrors}</FormErrorMessage>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-4">
          <WeeklyAssignmentEditor />
          <h3 className="font-bold">
            What dates would you like to apply these assignments to?
          </h3>
          <FormFieldController
            name={`applyDateRange.start`}
            rules={{ validate: validateRangeStartDate }}
          >
            {({ field }) => {
              return (
                <FormField
                  type="date"
                  label="Range Start"
                  inputProps={{
                    id: `applyDateRange-start-date`,
                    value: field.value,
                    min: today,
                    max: endDateValue || undefined,
                  }}
                  onChange={field.onChange}
                  error={errors.applyDateRange?.start?.message?.toString()}
                />
              );
            }}
          </FormFieldController>
          <FormFieldController
            name={`applyDateRange.end`}
            rules={{
              validate: (value) => {
                const pastCheck = validateRangeEndDate(value);
                if (pastCheck !== true) return pastCheck;
                if (value && startDateValue) {
                  const endDt = DateTime.fromISO(value);
                  const startDt = DateTime.fromISO(startDateValue);
                  if (endDt.isValid && startDt.isValid && endDt.startOf('day') < startDt.startOf('day')) {
                    return 'Range End must not be before Range Start';
                  }
                }
                if (value && maxEndDate && DateTime.fromISO(value).startOf('day') > DateTime.fromISO(maxEndDate).startOf('day')) {
                  return 'Range End must be within 2 months of Range Start';
                }
                return true;
              },
            }}
          >
            {({ field }) => {
              return (
                <FormField
                  type="date"
                  label="Range End (Max 2 months from start)"
                  inputProps={{
                    id: `applyDateRange-end-date`,
                    value: field.value,
                    min: startDateValue || today,
                    max: maxEndDate,
                  }}
                  onChange={field.onChange}
                  error={errors.applyDateRange?.end?.message?.toString()}
                />
              );
            }}
          </FormFieldController>
        </div>
        {children}
        <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <Button
            className="sm:col-span-3"
            onClick={() => {
              onCancel();
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="sm:col-span-3"
            type="submit"
            variant="primary"
            disabled={!isValid || loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
